import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Switch } from "../../ui/switch";
import { cn } from "../../utils/common/cn";
import {
  ALL_INDUSTRIES,
  ALL_OFFICES,
  ALL_RATINGS,
  POLICY_RATINGS,
  grossMarginFor,
  hasOwnPolicy,
  rbpEnabledFor,
  rbpEnabledForByIndustry,
  readOnlyContextFor,
  requiredYield,
  resolvePolicy,
  type PolicyContextRow,
  type PolicyRating,
  type PolicyRow,
  type PolicyRowByIndustry,
  type ReadOnlyContext,
  type SyncStatus,
  type TargetLogEntry,
  type TargetPolicy,
  type TargetProposal,
  type TargetProposalDraft,
} from "../../pages/AthenaPricingEngine/lib/api/manage-targets";

function pct(value: number) {
  return `${value.toFixed(2)}%`;
}

function rate(value: number) {
  return `${value.toFixed(1)}%`;
}

/**
 * A row shows up for a given (office[, industry]) as soon as EITHER the grid
 * has a row of its own for it OR `context` (from `wal_grouping_sic_office`,
 * see usePolicyContext) has real FTP/expected-loss/external-commission data
 * for it.
 *
 * The values shown are always REAL -- the grid is sparse but never empty, so
 * `resolvePolicy` falls back through the covering rows to the All/All/All
 * default rather than returning nothing. That is the same cascade the pricing
 * formulas use, so this table shows what the rest of the product computes with.
 *
 * The table stays at office / office×industry grain: rating-level rows exist in
 * the grid but would multiply this table sixfold, so they are visible through
 * the proposal dialog and the log instead.
 *
 * `offices` is the real, live office name list (see useOffices); `industries`
 * is the real, flat `sic_division` list (see useIndustries): there's no
 * confirmed per-office industry mapping, so every office is offered the same
 * full industry list as a potential override.
 */
export function buildPolicyRows(
  policies: TargetPolicy[],
  context: PolicyContextRow[],
  office: string,
  offices: string[],
  industries: string[],
): PolicyRow[] {
  const scopedOffices = office === ALL_OFFICES ? offices : [office];
  const rows: PolicyRow[] = [];

  for (const div of scopedOffices) {
    const officePolicy = resolvePolicy(policies, div);
    const officeEnabled = rbpEnabledFor(policies, div);
    const ownOfficeRow = hasOwnPolicy(policies, div);
    const hasOfficeContext = context.some((c) => c.office === div && c.industry == null);
    if (!ownOfficeRow && !hasOfficeContext) continue;

    rows.push({
      key: `${div}|default`,
      office: div,
      opex: officePolicy?.opex ?? null,
      targetNetMargin: officePolicy?.targetNetMargin ?? null,
      internalCommissionRate: officePolicy?.internalCommissionRate ?? null,
      rbpEnabled: officeEnabled,
      officeDisabled: !officeEnabled,
      updatedAt: ownOfficeRow ? (officePolicy?.updatedAt ?? null) : null,
      ownRow: ownOfficeRow,
    });

    for (const industry of industries) {
      const ownIndustryRow = hasOwnPolicy(policies, div, industry);
      const hasIndustryContext = context.some((c) => c.office === div && c.industry === industry);
      if (!ownIndustryRow && !hasIndustryContext) continue;

      const override = resolvePolicy(policies, div, industry);
      rows.push({
        key: `${div}|${industry}`,
        office: div,
        industry,
        opex: override?.opex ?? null,
        targetNetMargin: override?.targetNetMargin ?? null,
        internalCommissionRate: override?.internalCommissionRate ?? null,
        // The industry's OWN rung, not a copy of the office's. This used to read
        // officePolicy?.rbpEnabled, which is what made the switch office-only.
        rbpEnabled: rbpEnabledFor(policies, div, industry),
        officeDisabled: !officeEnabled,
        updatedAt: ownIndustryRow ? (override?.updatedAt ?? null) : null,
        ownRow: ownIndustryRow,
      });
    }
  }

  return rows;
}

/**
 * The REVERSE of buildPolicyRows: industry is the primary grouping, office the
 * breakdown underneath it. Same sparse-grid, same-real-values reasoning --
 * see buildPolicyRows' own docstring, which applies here unchanged with the two
 * dimensions swapped.
 *
 * `resolvePolicy(policies, ALL_OFFICES, ind)` is the industry rollup -- passing the
 * ALL_OFFICES sentinel as the office argument means only all-offices rows are matched
 * (either the industry's own All Offices/All Ratings row, or the All/All/All default),
 * mirroring exactly how buildPolicyRows' `resolvePolicy(policies, div)` only matches
 * all-industries rows for one office. `resolvePolicy(policies, off, ind)` for the
 * per-office breakdown is the SAME call buildPolicyRows already makes
 * (`resolvePolicy(policies, div, industry)`), just iterated the other way around --
 * resolvePolicy itself needed no changes, it was already symmetric in office/industry.
 */
export function buildPolicyRowsByIndustry(
  policies: TargetPolicy[],
  context: PolicyContextRow[],
  industry: string,
  offices: string[],
  industries: string[],
): PolicyRowByIndustry[] {
  const scopedIndustries = industry === ALL_INDUSTRIES ? industries : [industry];
  const rows: PolicyRowByIndustry[] = [];

  for (const ind of scopedIndustries) {
    const industryPolicy = resolvePolicy(policies, ALL_OFFICES, ind);
    const industryEnabled = rbpEnabledForByIndustry(policies, ind);
    const ownIndustryRow = policies.some((p) => p.office === ALL_OFFICES && p.industry === ind);
    // Context is inherently office-keyed (wal_grouping_sic_office); there is no single
    // "all offices" context row for an industry, so existence is "does AT LEAST ONE
    // office report real data for this industry" rather than one exact row lookup.
    const hasIndustryContext = context.some((c) => c.industry === ind);
    if (!ownIndustryRow && !hasIndustryContext) continue;

    rows.push({
      key: `${ind}|default`,
      industry: ind,
      opex: industryPolicy?.opex ?? null,
      targetNetMargin: industryPolicy?.targetNetMargin ?? null,
      internalCommissionRate: industryPolicy?.internalCommissionRate ?? null,
      rbpEnabled: industryEnabled,
      industryDisabled: !industryEnabled,
      updatedAt: ownIndustryRow ? (industryPolicy?.updatedAt ?? null) : null,
      ownRow: ownIndustryRow,
    });

    for (const office of offices) {
      const ownOfficeRow = hasOwnPolicy(policies, office, ind);
      const hasOfficeContext = context.some((c) => c.office === office && c.industry === ind);
      if (!ownOfficeRow && !hasOfficeContext) continue;

      const override = resolvePolicy(policies, office, ind);
      rows.push({
        key: `${ind}|${office}`,
        industry: ind,
        office,
        opex: override?.opex ?? null,
        targetNetMargin: override?.targetNetMargin ?? null,
        internalCommissionRate: override?.internalCommissionRate ?? null,
        // The office's OWN rung under this industry, not a copy of the industry's.
        rbpEnabled: rbpEnabledForByIndustry(policies, ind, office),
        industryDisabled: !industryEnabled,
        updatedAt: ownOfficeRow ? (override?.updatedAt ?? null) : null,
        ownRow: ownOfficeRow,
      });
    }
  }

  return rows;
}

/** Single non-wrapping row of office tabs with click-to-scroll arrows -- a
 * real office list (15 offices + "All offices") wraps into 2 rows under
 * plain flex-wrap, so this scrolls horizontally instead, staying on one
 * row at any width. */
export function OfficeTabs({
  value,
  onChange,
  offices,
}: {
  value: string;
  onChange: (office: string) => void;
  offices: string[];
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState, offices]);

  function scrollByAmount(direction: 1 | -1) {
    scrollRef.current?.scrollBy({ left: direction * 240, behavior: "smooth" });
  }

  return (
    <div className="flex items-stretch border border-border bg-secondary/40">
      <button
        type="button"
        aria-label="Scroll Offices Left"
        disabled={!canScrollLeft}
        onClick={() => scrollByAmount(-1)}
        className="flex shrink-0 items-center justify-center border-r border-border px-2 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
      >
        <ChevronLeft className="size-4" />
      </button>
      <div ref={scrollRef} className="flex flex-1 flex-nowrap overflow-x-hidden scroll-smooth">
        {[ALL_OFFICES, ...offices].map((office) => (
          <button
            key={office}
            type="button"
            onClick={() => onChange(office)}
            className={cn(
              "shrink-0 whitespace-nowrap border-r border-border px-4 py-2 text-xs last:border-r-0",
              value === office
                ? "bg-amber-dim font-semibold text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {office}
          </button>
        ))}
      </div>
      <button
        type="button"
        aria-label="Scroll Offices Right"
        disabled={!canScrollRight}
        onClick={() => scrollByAmount(1)}
        className="flex shrink-0 items-center justify-center border-l border-border px-2 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  );
}

/** Same as OfficeTabs, industry-keyed. Kept as a separate component rather than a
 *  parameterized OfficeTabs -- the two lists (offices vs industries) come from different
 *  hooks and have no shared identity beyond both being string lists, so a shared prop
 *  would just be `items`/`allValue` with no real behaviour in common worth abstracting. */
export function IndustryTabs({
  value,
  onChange,
  industries,
}: {
  value: string;
  onChange: (industry: string) => void;
  industries: string[];
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState, industries]);

  function scrollByAmount(direction: 1 | -1) {
    scrollRef.current?.scrollBy({ left: direction * 240, behavior: "smooth" });
  }

  return (
    <div className="flex items-stretch border border-border bg-secondary/40">
      <button
        type="button"
        aria-label="Scroll Industries Left"
        disabled={!canScrollLeft}
        onClick={() => scrollByAmount(-1)}
        className="flex shrink-0 items-center justify-center border-r border-border px-2 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
      >
        <ChevronLeft className="size-4" />
      </button>
      <div ref={scrollRef} className="flex flex-1 flex-nowrap overflow-x-hidden scroll-smooth">
        {[ALL_INDUSTRIES, ...industries].map((industry) => (
          <button
            key={industry}
            type="button"
            onClick={() => onChange(industry)}
            className={cn(
              "shrink-0 whitespace-nowrap border-r border-border px-4 py-2 text-xs last:border-r-0",
              value === industry
                ? "bg-amber-dim font-semibold text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {industry}
          </button>
        ))}
      </div>
      <button
        type="button"
        aria-label="Scroll Industries Right"
        disabled={!canScrollRight}
        onClick={() => scrollByAmount(1)}
        className="flex shrink-0 items-center justify-center border-l border-border px-2 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  );
}

/** Office-level RBP switch. Every change needs a reason and is logged. */
export function RbpEnabledPanel({
  office,
  enabled,
  onPropose,
  canPropose,
}: {
  office: string;
  enabled: boolean;
  onPropose: (office: string, next: boolean) => void;
  /** Flipping the switch raises an rbp-toggle proposal, so it is a propose action and
   *  takes the reviewer gate. Below that the panel still states the current setting. */
  canPropose: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border border-border bg-secondary/30 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-foreground">
          Risk-based pricing in {office}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {enabled
            ? "Deals in this office are priced from the target policy below."
            : "This office prices off a fixed rate card — RBPE reports on it but does not price it."}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {canPropose && (
          <Switch
            id={`rbp-${office}`}
            checked={enabled}
            onCheckedChange={(next) => onPropose(office, next)}
          />
        )}
        <Label htmlFor={`rbp-${office}`} className="text-xs">
          {enabled ? "Enabled" : "Disabled"}
        </Label>
      </div>
    </div>
  );
}

/** Industry-level RBP switch -- the dominant rung of the reverse ladder, mirroring
 *  RbpEnabledPanel exactly. Every change needs a reason and is logged, same as the
 *  office-led panel; both raise the same `kind: "rbp-toggle"` proposal shape. */
export function IndustryEnabledPanel({
  industry,
  enabled,
  onPropose,
  canPropose,
  hasOwnOfficeOverrides,
}: {
  industry: string;
  enabled: boolean;
  onPropose: (industry: string, next: boolean) => void;
  /** Flipping the switch raises an rbp-toggle proposal, so it is a propose action and
   *  takes the reviewer gate. Below that the panel still states the current setting. */
  canPropose: boolean;
  /** True when at least one office already has its OWN decided value under this
   *  industry -- surfaced as a plain note, not a gate. That office's row is the same
   *  physical (office, industry, All Ratings) row the OFFICE-led ladder's per-industry
   *  switch would have set; a value decided under that ladder does not automatically
   *  mean the same thing here, so it's worth a nudge to confirm rather than assuming. */
  hasOwnOfficeOverrides?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-4 border border-border bg-secondary/30 px-4 py-3">
        <div>
          <p className="text-sm font-medium text-foreground">
            Risk-based pricing for {industry}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {enabled
              ? "Deals in this industry, in every office, are priced from the target policy below."
              : "This industry prices off a fixed rate card everywhere — RBPE reports on it but does not price it."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canPropose && (
            <Switch
              id={`rbp-industry-${industry}`}
              checked={enabled}
              onCheckedChange={(next) => onPropose(industry, next)}
            />
          )}
          <Label htmlFor={`rbp-industry-${industry}`} className="text-xs">
            {enabled ? "Enabled" : "Disabled"}
          </Label>
        </div>
      </div>
      {hasOwnOfficeOverrides ? (
        <p className="px-1 text-[11px] text-muted-foreground">
          One or more offices already have a setting for {industry} under Manage Targets'
          office-led view — confirm each still reflects what you want here.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The third grid dimension: an (office, industry) pair has one row PER
 * RATING in `target_policies`, but neither ActivePolicyTable nor
 * ActivePolicyTableByIndustry ever surfaced it -- everything shown was
 * resolved at rating = ALL_RATINGS, so a real per-rating override was in the
 * database but invisible here. Unlike Office/Industry rows (which only show
 * up with an own row or own context), every real rating always shows here --
 * there's no rating-scoped context (wal_grouping_sic_office has no rating
 * column) to gate on, and the point is to show what a rating WOULD price at
 * whether or not anyone has ever overridden it specifically, same as
 * `defaultsFor` already does for the propose dialog.
 */
interface RatingRow {
  key: string;
  rating: PolicyRating;
  opex: number | null;
  targetNetMargin: number | null;
  internalCommissionRate: number | null;
  updatedAt: string | null;
  ownRow: boolean;
}

function buildRatingRows(policies: TargetPolicy[], office: string, industry: string): RatingRow[] {
  return POLICY_RATINGS.map((rating) => {
    const own = policies.find(
      (p) => p.office === office && p.industry === industry && p.rating === rating,
    );
    const resolved = resolvePolicy(policies, office, industry, rating);
    return {
      key: `${office}|${industry}|${rating}`,
      rating,
      opex: resolved?.opex ?? null,
      targetNetMargin: resolved?.targetNetMargin ?? null,
      internalCommissionRate: resolved?.internalCommissionRate ?? null,
      updatedAt: own ? own.updatedAt : null,
      ownRow: own != null,
    };
  });
}

/** FTP through Required Yield -- identical math at every grain (office,
 *  industry or rating), so office/industry/rating rows all render these eight
 *  cells through here instead of three near-duplicate blocks. */
function MetricCells({
  rowContext,
  opex,
  targetNetMargin,
  internalCommissionRate,
}: {
  rowContext: ReadOnlyContext;
  opex: number | null;
  targetNetMargin: number | null;
  internalCommissionRate: number | null;
}) {
  const hasPolicy = opex != null && targetNetMargin != null && internalCommissionRate != null;
  const grossMargin = hasPolicy ? grossMarginFor(targetNetMargin!, internalCommissionRate!) : null;
  return (
    <>
      <td className="px-3 py-2.5 tabular-nums text-muted-foreground">{pct(rowContext.ftp)}</td>
      <td className="px-3 py-2.5 tabular-nums text-muted-foreground">{pct(rowContext.expectedLoss)}</td>
      <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
        {pct(rowContext.externalCommission)}
      </td>
      <td className="px-3 py-2.5 tabular-nums text-foreground">{opex != null ? pct(opex) : "—"}</td>
      <td className="px-3 py-2.5 tabular-nums text-foreground">
        {targetNetMargin != null ? pct(targetNetMargin) : "—"}
      </td>
      <td className="px-3 py-2.5 tabular-nums text-foreground">
        {internalCommissionRate != null ? rate(internalCommissionRate) : "—"}
      </td>
      <td className="px-3 py-2.5 tabular-nums font-medium text-foreground">
        {grossMargin != null ? pct(grossMargin) : "—"}
      </td>
      <td className="px-3 py-2.5 tabular-nums font-semibold text-primary">
        {hasPolicy
          ? pct(requiredYield(rowContext, opex!, targetNetMargin!, internalCommissionRate!))
          : "—"}
      </td>
    </>
  );
}

/** Chevron toggle shared by every expandable row at every grain -- same
 *  bordered-square button Segments' own "Segment" column and the Loss
 *  Calibration ledger use, so the drill-down interaction reads the same way
 *  everywhere on this product. */
function ExpandToggle({ expanded, onClick, label }: { expanded: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className="flex size-5 items-center justify-center border border-border text-muted-foreground hover:text-foreground"
      aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
      onClick={onClick}
    >
      {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
    </button>
  );
}

interface OfficeGroup {
  office: PolicyRow;
  industries: PolicyRow[];
}

/** Reconstructs the Office -> Industry tree from buildPolicyRows' flat
 *  output. Relies on that function's own ordering (an office's row always
 *  comes immediately before its own industry rows) rather than changing its
 *  return shape -- the server-paginated /target-policies/page endpoint
 *  returns the identical flat shape and ordering, so this grouping works
 *  unchanged for both the single-office and "All Offices" views. */
function groupByOffice(rows: PolicyRow[]): OfficeGroup[] {
  const groups: OfficeGroup[] = [];
  for (const row of rows) {
    if (row.industry == null) groups.push({ office: row, industries: [] });
    else if (groups.length) groups[groups.length - 1]!.industries.push(row);
  }
  return groups;
}

interface IndustryGroup {
  industry: PolicyRowByIndustry;
  offices: PolicyRowByIndustry[];
}

/** The REVERSE of groupByOffice -- same ordering reliance on
 *  buildPolicyRowsByIndustry (and the industry-ladder half of
 *  /target-policies/page), roles of office/industry swapped. */
function groupByIndustry(rows: PolicyRowByIndustry[]): IndustryGroup[] {
  const groups: IndustryGroup[] = [];
  for (const row of rows) {
    if (row.office == null) groups.push({ industry: row, offices: [] });
    else if (groups.length) groups[groups.length - 1]!.offices.push(row);
  }
  return groups;
}

export function ActivePolicyTable({
  rows,
  context,
  policies,
  onPropose,
  onToggleRbp,
  canPropose,
}: {
  rows: PolicyRow[];
  context: PolicyContextRow[];
  /** The full, unpaginated grid -- needed here (not just by buildPolicyRows)
   *  because every industry row's Rating drill-down is resolved on demand,
   *  only while expanded, rather than precomputed for every row up front. */
  policies: TargetPolicy[];
  onPropose: (office: string, industry?: string, rating?: PolicyRating) => void;
  /** Raises an rbp-toggle for one rung of the ladder. `industry` undefined means
   *  the office rung. */
  onToggleRbp: (office: string, industry: string | undefined, next: boolean) => void;
  /** Reviewer and above. Raising a target change is the reviewer's half of the
   *  maker-checker flow; the backend gates POST /target-proposals to match. */
  canPropose: boolean;
}) {
  // Office -> Rating -> Industry. An office row opens onto its ratings (resolved at office level, industry =
  // All), and each rating opens onto that office's industries at that rating. Every real rating shows,
  // whether or not it has its own override -- see buildRatingRows. The pricing resolution is unchanged; only
  // the order of the drill-down is.
  const [expandedOffices, setExpandedOffices] = useState<Record<string, boolean>>({});
  const [expandedRatings, setExpandedRatings] = useState<Record<string, boolean>>({});
  const groups = useMemo(() => groupByOffice(rows), [rows]);

  function segmentsLink(office: string, industry?: string) {
    // level=Office -- without it Segments always opens on its own
    // hardcoded default (Industry) regardless of which ladder this
    // link came from, dropping the Office filter along with it since
    // that filter no longer applies once landed on the wrong tab.
    const params = new URLSearchParams({ office, level: "Office" });
    if (industry) params.set("industry", industry);
    return `/risk-pricing-engine/segments?${params.toString()}`;
  }

  return (
    <div className="overflow-x-auto border border-border bg-card">
      <table className="w-full min-w-[1360px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            {[
              "",
              "Office Name",
              "Rating",
              "Industry",
              "FTP",
              "Expected Loss",
              "External Commission",
              "OpEx",
              "Target Net Margin",
              "Internal Commission Rate",
              "Gross Margin",
              "Required Yield",
              "Priced",
              "Last Updated",
              "",
            ].map((head, i) => (
              <th
                key={`${head}-${i}`}
                className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map(({ office: officeRow, industries }) => {
            const officeContext = readOnlyContextFor(context, officeRow.office);
            const officeExpanded = !!expandedOffices[officeRow.office];
            // The office's own rating rows: industry = All, so these are the office-wide figures per rating.
            const officeRatingRows = officeExpanded
              ? buildRatingRows(policies, officeRow.office, ALL_INDUSTRIES)
              : [];
            return (
              <Fragment key={officeRow.key}>
                <tr
                  className={cn(
                    "border-b border-border/60 last:border-b-0",
                    !officeRow.rbpEnabled && "opacity-60",
                  )}
                >
                  <td className="px-2 py-2.5">
                    <ExpandToggle
                      expanded={officeExpanded}
                      label={`${officeRow.office}'s ratings`}
                      onClick={() =>
                        setExpandedOffices((e) => ({ ...e, [officeRow.office]: !e[officeRow.office] }))
                      }
                    />
                  </td>
                  <td className="px-3 py-2.5 font-medium text-foreground">{officeRow.office}</td>
                  <td className="px-3 py-2.5 text-foreground/90" />
                  <td className="px-3 py-2.5 text-foreground/90" />
                  <MetricCells
                    rowContext={officeContext}
                    opex={officeRow.opex}
                    targetNetMargin={officeRow.targetNetMargin}
                    internalCommissionRate={officeRow.internalCommissionRate}
                  />
                  <td className="px-3 py-2.5">
                    {canPropose ? (
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={officeRow.rbpEnabled}
                          onCheckedChange={(next) => onToggleRbp(officeRow.office, undefined, next)}
                          aria-label={`Risk-based pricing in ${officeRow.office}`}
                        />
                        <span className="text-xs text-muted-foreground">
                          {officeRow.rbpEnabled ? "On" : "Off"}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {officeRow.rbpEnabled ? "On" : "Off"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">
                    {officeRow.updatedAt ? format(new Date(officeRow.updatedAt), "d MMM yyyy") : "inherited"}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button asChild size="sm" variant="ghost" className="h-8 text-xs">
                        <Link to={segmentsLink(officeRow.office)}>View in Segments</Link>
                      </Button>
                      {canPropose && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs"
                          onClick={() => onPropose(officeRow.office)}
                        >
                          Propose Change
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>

                {officeExpanded &&
                  officeRatingRows.map((ratingRow) => {
                    const ratingKey = `${officeRow.office}|${ratingRow.rating}`;
                    const ratingExpanded = !!expandedRatings[ratingKey];
                    return (
                      <Fragment key={ratingKey}>
                        <tr className="border-b border-border/60 last:border-b-0 bg-secondary/20">
                          <td className="px-2 py-2.5 pl-6">
                            {industries.length ? (
                              <ExpandToggle
                                expanded={ratingExpanded}
                                label={`${ratingRow.rating} industries in ${officeRow.office}`}
                                onClick={() =>
                                  setExpandedRatings((e) => ({ ...e, [ratingKey]: !e[ratingKey] }))
                                }
                              />
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5" />
                          <td className="px-3 py-2.5 text-foreground/90">{ratingRow.rating}</td>
                          <td className="px-3 py-2.5" />
                          <MetricCells
                            rowContext={officeContext}
                            opex={ratingRow.opex}
                            targetNetMargin={ratingRow.targetNetMargin}
                            internalCommissionRate={ratingRow.internalCommissionRate}
                          />
                          <td className="px-3 py-2.5 text-xs text-muted-foreground">
                            {/* No per-rating RBP rung exists -- see target_policy.py's
                                resolve_rbp_enabled docstring -- so this mirrors the office
                                row rather than offering a switch that has nothing real to
                                write to. */}
                            {officeRow.rbpEnabled ? "On" : "Off"}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-muted-foreground">
                            {ratingRow.updatedAt
                              ? format(new Date(ratingRow.updatedAt), "d MMM yyyy")
                              : "inherited"}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button asChild size="sm" variant="ghost" className="h-8 text-xs">
                                <Link to={segmentsLink(officeRow.office)}>View in Segments</Link>
                              </Button>
                              {canPropose && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-xs"
                                  onClick={() => onPropose(officeRow.office, undefined, ratingRow.rating)}
                                >
                                  Propose Change
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {ratingExpanded &&
                          industries.map((industryRow) => {
                            const industryContext = readOnlyContextFor(
                              context,
                              industryRow.office,
                              industryRow.industry,
                            );
                            // This industry's figures at THIS rating (office, industry, rating).
                            const industryAtRating = buildRatingRows(
                              policies,
                              industryRow.office,
                              industryRow.industry!,
                            ).find((r) => r.rating === ratingRow.rating)!;
                            return (
                              <tr
                                key={`${ratingKey}|${industryRow.industry}`}
                                className={cn(
                                  "border-b border-border/60 last:border-b-0",
                                  !industryRow.rbpEnabled && "opacity-60",
                                )}
                              >
                                <td className="px-2 py-2.5" />
                                <td className="px-3 py-2.5" />
                                <td className="px-3 py-2.5" />
                                <td className="px-3 py-2.5 pl-6 text-foreground/90">{industryRow.industry}</td>
                                <MetricCells
                                  rowContext={industryContext}
                                  opex={industryAtRating.opex}
                                  targetNetMargin={industryAtRating.targetNetMargin}
                                  internalCommissionRate={industryAtRating.internalCommissionRate}
                                />
                                <td className="px-3 py-2.5">
                                  {canPropose ? (
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={industryRow.rbpEnabled}
                                        // The switch is the (office, industry) rung, shared by every
                                        // rating row it appears under. An industry inside a switched-off
                                        // office cannot be turned on -- office-off wins in resolution, so
                                        // an enabled-looking switch there would be a lie. The office's own
                                        // row stays operable, which is how you get the office back on.
                                        disabled={officeRow.officeDisabled}
                                        onCheckedChange={(next) =>
                                          onToggleRbp(industryRow.office, industryRow.industry, next)
                                        }
                                        aria-label={`Risk-based pricing for ${industryRow.industry} in ${industryRow.office}`}
                                      />
                                      <span className="text-xs text-muted-foreground">
                                        {officeRow.officeDisabled
                                          ? "Office Off"
                                          : industryRow.rbpEnabled
                                            ? "On"
                                            : "Office Targets"}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">
                                      {industryRow.rbpEnabled ? "On" : "Office Targets"}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-muted-foreground">
                                  {industryAtRating.updatedAt
                                    ? format(new Date(industryAtRating.updatedAt), "d MMM yyyy")
                                    : "inherited"}
                                </td>
                                <td className="px-3 py-2.5 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button asChild size="sm" variant="ghost" className="h-8 text-xs">
                                      <Link to={segmentsLink(industryRow.office, industryRow.industry)}>
                                        View in Segments
                                      </Link>
                                    </Button>
                                    {canPropose && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 text-xs"
                                        onClick={() =>
                                          onPropose(industryRow.office, industryRow.industry, ratingRow.rating)
                                        }
                                      >
                                        Propose Change
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </Fragment>
                    );
                  })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The REVERSE of ActivePolicyTable: "Industry Name" / "Office" column order and copy
 * instead of "Office Name" / "Industry". A sibling, not a parameterized version of
 * ActivePolicyTable -- threading a `primary` prop through every column header, aria-label
 * and status-text branch below would risk the existing, working office-led table for a
 * table that's read far more often than it's changed. Keep the two in step by hand if one
 * changes structurally; they are not expected to drift on copy alone.
 */
export function ActivePolicyTableByIndustry({
  rows,
  context,
  policies,
  onPropose,
  onToggleRbp,
  canPropose,
}: {
  rows: PolicyRowByIndustry[];
  context: PolicyContextRow[];
  /** See ActivePolicyTable's own doc comment on this prop -- same reason. */
  policies: TargetPolicy[];
  onPropose: (industry: string, office?: string, rating?: PolicyRating) => void;
  /** Raises an rbp-toggle for one rung of the reverse ladder. `office` undefined means
   *  the industry rung. */
  onToggleRbp: (industry: string, office: string | undefined, next: boolean) => void;
  canPropose: boolean;
}) {
  // Industry -> Rating -> Office, the mirror of ActivePolicyTable: an industry row opens onto its ratings
  // (resolved across all offices), and each rating opens onto that industry's offices at that rating.
  const [expandedIndustries, setExpandedIndustries] = useState<Record<string, boolean>>({});
  const [expandedRatings, setExpandedRatings] = useState<Record<string, boolean>>({});
  const groups = useMemo(() => groupByIndustry(rows), [rows]);

  function segmentsLink(industry: string, office?: string) {
    // level=Industry, matching the ActivePolicyTable link's level=Office --
    // same reason: seed Segments' initial tab explicitly instead of
    // relying on its hardcoded default.
    const params = new URLSearchParams({ industry, level: "Industry" });
    if (office) params.set("office", office);
    return `/risk-pricing-engine/segments?${params.toString()}`;
  }

  return (
    <div className="overflow-x-auto border border-border bg-card">
      <table className="w-full min-w-[1360px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            {[
              "",
              "Industry Name",
              "Rating",
              "Office",
              "FTP",
              "Expected Loss",
              "External Commission",
              "OpEx",
              "Target Net Margin",
              "Internal Commission Rate",
              "Gross Margin",
              "Required Yield",
              "Priced",
              "Last Updated",
              "",
            ].map((head, i) => (
              <th
                key={`${head}-${i}`}
                className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map(({ industry: industryRow, offices }) => {
            // readOnlyContextFor already handles an ALL_OFFICES average -- reused unchanged
            // for the industry rollup row, exactly as ActivePolicyTable reuses it for a
            // per-office row.
            const industryContext = readOnlyContextFor(context, ALL_OFFICES, industryRow.industry);
            const industryExpanded = !!expandedIndustries[industryRow.industry];
            // The industry's own rating rows: office = All, so these are the industry-wide figures per rating.
            const industryRatingRows = industryExpanded
              ? buildRatingRows(policies, ALL_OFFICES, industryRow.industry)
              : [];
            return (
              <Fragment key={industryRow.key}>
                <tr
                  className={cn(
                    "border-b border-border/60 last:border-b-0",
                    !industryRow.rbpEnabled && "opacity-60",
                  )}
                >
                  <td className="px-2 py-2.5">
                    <ExpandToggle
                      expanded={industryExpanded}
                      label={`${industryRow.industry}'s ratings`}
                      onClick={() =>
                        setExpandedIndustries((e) => ({
                          ...e,
                          [industryRow.industry]: !e[industryRow.industry],
                        }))
                      }
                    />
                  </td>
                  <td className="px-3 py-2.5 font-medium text-foreground">{industryRow.industry}</td>
                  <td className="px-3 py-2.5 text-foreground/90" />
                  <td className="px-3 py-2.5 text-foreground/90" />
                  <MetricCells
                    rowContext={industryContext}
                    opex={industryRow.opex}
                    targetNetMargin={industryRow.targetNetMargin}
                    internalCommissionRate={industryRow.internalCommissionRate}
                  />
                  <td className="px-3 py-2.5">
                    {canPropose ? (
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={industryRow.rbpEnabled}
                          onCheckedChange={(next) => onToggleRbp(industryRow.industry, undefined, next)}
                          aria-label={`Risk-based pricing for ${industryRow.industry}`}
                        />
                        <span className="text-xs text-muted-foreground">
                          {industryRow.rbpEnabled ? "On" : "Off"}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {industryRow.rbpEnabled ? "On" : "Off"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">
                    {industryRow.updatedAt
                      ? format(new Date(industryRow.updatedAt), "d MMM yyyy")
                      : "inherited"}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button asChild size="sm" variant="ghost" className="h-8 text-xs">
                        <Link to={segmentsLink(industryRow.industry)}>View in Segments</Link>
                      </Button>
                      {canPropose && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs"
                          onClick={() => onPropose(industryRow.industry)}
                        >
                          Propose Change
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>

                {industryExpanded &&
                  industryRatingRows.map((ratingRow) => {
                    const ratingKey = `${industryRow.industry}|${ratingRow.rating}`;
                    const ratingExpanded = !!expandedRatings[ratingKey];
                    return (
                      <Fragment key={ratingKey}>
                        <tr className="border-b border-border/60 last:border-b-0 bg-secondary/20">
                          <td className="px-2 py-2.5 pl-6">
                            {offices.length ? (
                              <ExpandToggle
                                expanded={ratingExpanded}
                                label={`${ratingRow.rating} offices in ${industryRow.industry}`}
                                onClick={() =>
                                  setExpandedRatings((e) => ({ ...e, [ratingKey]: !e[ratingKey] }))
                                }
                              />
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5" />
                          <td className="px-3 py-2.5 text-foreground/90">{ratingRow.rating}</td>
                          <td className="px-3 py-2.5" />
                          <MetricCells
                            rowContext={industryContext}
                            opex={ratingRow.opex}
                            targetNetMargin={ratingRow.targetNetMargin}
                            internalCommissionRate={ratingRow.internalCommissionRate}
                          />
                          <td className="px-3 py-2.5 text-xs text-muted-foreground">
                            {/* No per-rating RBP rung exists -- mirrors the industry row rather
                                than a switch with nothing to write to. */}
                            {industryRow.rbpEnabled ? "On" : "Off"}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-muted-foreground">
                            {ratingRow.updatedAt
                              ? format(new Date(ratingRow.updatedAt), "d MMM yyyy")
                              : "inherited"}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button asChild size="sm" variant="ghost" className="h-8 text-xs">
                                <Link to={segmentsLink(industryRow.industry)}>View in Segments</Link>
                              </Button>
                              {canPropose && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-xs"
                                  onClick={() => onPropose(industryRow.industry, undefined, ratingRow.rating)}
                                >
                                  Propose Change
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {ratingExpanded &&
                          offices.map((officeRow) => {
                            const officeContext = readOnlyContextFor(
                              context,
                              officeRow.office!,
                              officeRow.industry,
                            );
                            // This office's figures at THIS rating (office, industry, rating).
                            const officeAtRating = buildRatingRows(
                              policies,
                              officeRow.office!,
                              officeRow.industry,
                            ).find((r) => r.rating === ratingRow.rating)!;
                            return (
                              <tr
                                key={`${ratingKey}|${officeRow.office}`}
                                className={cn(
                                  "border-b border-border/60 last:border-b-0",
                                  !officeRow.rbpEnabled && "opacity-60",
                                )}
                              >
                                <td className="px-2 py-2.5" />
                                <td className="px-3 py-2.5" />
                                <td className="px-3 py-2.5" />
                                <td className="px-3 py-2.5 pl-6 text-foreground/90">{officeRow.office}</td>
                                <MetricCells
                                  rowContext={officeContext}
                                  opex={officeAtRating.opex}
                                  targetNetMargin={officeAtRating.targetNetMargin}
                                  internalCommissionRate={officeAtRating.internalCommissionRate}
                                />
                                <td className="px-3 py-2.5">
                                  {canPropose ? (
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={officeRow.rbpEnabled}
                                        // The switch is the (industry, office) rung, shared by every
                                        // rating row it appears under. An office inside a switched-off
                                        // industry cannot be turned on -- industry-off wins in
                                        // resolution, so an enabled-looking switch there would be a lie.
                                        // The industry's own row stays operable, which is how you get
                                        // the industry back on.
                                        disabled={industryRow.industryDisabled}
                                        onCheckedChange={(next) =>
                                          onToggleRbp(industryRow.industry, officeRow.office, next)
                                        }
                                        aria-label={`Risk-based pricing for ${officeRow.office} under ${industryRow.industry}`}
                                      />
                                      <span className="text-xs text-muted-foreground">
                                        {industryRow.industryDisabled
                                          ? "Industry Off"
                                          : officeRow.rbpEnabled
                                            ? "On"
                                            : "Industry Targets"}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">
                                      {officeRow.rbpEnabled ? "On" : "Industry Targets"}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-muted-foreground">
                                  {officeAtRating.updatedAt
                                    ? format(new Date(officeAtRating.updatedAt), "d MMM yyyy")
                                    : "inherited"}
                                </td>
                                <td className="px-3 py-2.5 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button asChild size="sm" variant="ghost" className="h-8 text-xs">
                                      <Link to={segmentsLink(industryRow.industry, officeRow.office)}>
                                        View in Segments
                                      </Link>
                                    </Button>
                                    {canPropose && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 text-xs"
                                        onClick={() =>
                                          onPropose(industryRow.industry, officeRow.office, ratingRow.rating)
                                        }
                                      >
                                        Propose Change
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </Fragment>
                    );
                  })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export interface CurrentValues {
  opex: number | null;
  targetNetMargin: number | null;
  internalCommissionRate: number | null;
}

export function TargetProposalDialog({
  open,
  onOpenChange,
  initial,
  lockOffice,
  currentFor,
  defaultsFor,
  onSubmit,
  isSubmitting,
  offices,
  industries,
  context,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: { office: string; industry?: string; rating?: PolicyRating };
  lockOffice: boolean;
  currentFor: (office: string, industry?: string, rating?: PolicyRating) => CurrentValues;
  defaultsFor: (office: string) => CurrentValues | undefined;
  onSubmit: (draft: TargetProposalDraft) => Promise<unknown> | void;
  isSubmitting: boolean;
  offices: string[];
  industries: string[];
  context: PolicyContextRow[];
}) {
  const [office, setOffice] = useState(initial.office);
  const [industry, setIndustry] = useState(initial.industry ?? ALL_INDUSTRIES);
  const [rating, setRating] = useState<PolicyRating>(initial.rating ?? ALL_RATINGS);
  const [opex, setOpex] = useState("");
  const [netMargin, setNetMargin] = useState("");
  const [commissionRate, setCommissionRate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // The sentinels ARE what gets submitted and stored; `undefined` here only
  // narrows the lookups below, which read "all" as "no filter on this axis".
  const industryValue = industry === ALL_INDUSTRIES ? undefined : industry;
  const ratingValue = rating === ALL_RATINGS ? undefined : rating;
  const current = currentFor(office, industryValue, ratingValue);
  const readOnlyContext = readOnlyContextFor(context, office, industryValue);

  useEffect(() => {
    if (!open) return;
    setOffice(initial.office);
    setIndustry(initial.industry ?? ALL_INDUSTRIES);
    setRating(initial.rating ?? ALL_RATINGS);
    setReason("");
    setError(null);
    const seed = currentFor(initial.office, initial.industry, initial.rating);
    const fallback = defaultsFor(initial.office);
    setOpex(String(seed.opex ?? fallback?.opex ?? ""));
    setNetMargin(String(seed.targetNetMargin ?? fallback?.targetNetMargin ?? ""));
    setCommissionRate(
      String(seed.internalCommissionRate ?? fallback?.internalCommissionRate ?? ""),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial.office, initial.industry, initial.rating]);

  const opexNumber = Number(opex);
  const netMarginNumber = Number(netMargin);
  const rateNumber = Number(commissionRate);
  const valid =
    Number.isFinite(opexNumber) && Number.isFinite(netMarginNumber) && Number.isFinite(rateNumber);
  const previewGrossMargin = valid ? grossMarginFor(netMarginNumber || 0, rateNumber || 0) : 0;
  const preview = valid
    ? requiredYield(readOnlyContext, opexNumber || 0, netMarginNumber || 0, rateNumber || 0)
    : readOnlyContext.ftp + readOnlyContext.expectedLoss + readOnlyContext.externalCommission;

  function handleSubmit() {
    if (!(opexNumber > 0) || !(netMarginNumber > 0)) {
      setError("Enter a positive OpEx and target net margin — both are rates, not adjustments.");
      return;
    }
    if (!(rateNumber >= 0) || rateNumber >= 95) {
      setError("Internal commission rate must be between 0 and 95 percent of gross margin.");
      return;
    }
    if (!reason.trim()) {
      setError("Add a reason. Every target change is logged with why it was made.");
      return;
    }
    setError(null);
    void onSubmit({
      kind: "targets",
      office,
      // Always sent, sentinel included: "all" is a stored value now, not an
      // omission the server has to infer.
      industry,
      rating,
      currentOpex: current.opex,
      proposedOpex: Number(opexNumber.toFixed(2)),
      currentTargetNetMargin: current.targetNetMargin,
      proposedTargetNetMargin: Number(netMarginNumber.toFixed(2)),
      currentInternalCommissionRate: current.internalCommissionRate,
      proposedInternalCommissionRate: Number(rateNumber.toFixed(1)),
      reason: reason.trim(),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Propose A Target Change</DialogTitle>
          <DialogDescription>
            OpEx, target net margin and internal commission rate are proposed here. FTP, expected
            loss and external commission are read-only context.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Office Name</Label>
              {lockOffice ? (
                <div className="border border-border bg-secondary/40 px-3 py-2 text-sm text-foreground">
                  {office}
                </div>
              ) : (
                <Select
                  value={office}
                  onValueChange={(value) => {
                    setOffice(value);
                    setIndustry(ALL_INDUSTRIES);
                    setRating(ALL_RATINGS);
                  }}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_OFFICES}>{ALL_OFFICES}</SelectItem>
                    {offices.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label>Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* "Office default" and "All Industries" both submitted the
                      same thing, so they were two labels for one scope. Kept as
                      the single option it always was -- now a stored value. */}
                  <SelectItem value={ALL_INDUSTRIES}>{ALL_INDUSTRIES}</SelectItem>
                  {industries.map((i) => (
                    <SelectItem key={i} value={i}>
                      {i}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Rating</Label>
              {/* No longer gated on picking an industry first: (All Industries,
                  one office, one rating) is a legitimate scope in the grid, and
                  approving it fans out across every industry. */}
              <Select value={rating} onValueChange={(value) => setRating(value as PolicyRating)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_RATINGS}>{ALL_RATINGS}</SelectItem>
                  {POLICY_RATINGS.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-3 border border-border bg-secondary/30 p-3 sm:grid-cols-3">
            <ReadOnlyValue
              label={`FTP (WAL ${readOnlyContext.walYears.toFixed(2)} yrs)`}
              value={pct(readOnlyContext.ftp)}
            />
            <ReadOnlyValue
              label="Expected Loss (Calibration)"
              value={pct(readOnlyContext.expectedLoss)}
            />
            <ReadOnlyValue
              label="External Commission"
              value={pct(readOnlyContext.externalCommission)}
            />
          </div>

          {/* "Internal Commission Rate %" wraps to two lines where the other two labels
              fit on one, which used to push its input and its Current: line lower than
              the others. Rather than pick a min-height that a longer label would break
              again, each cell is a full-height flex column with the input block pushed
              down by mt-auto: grid stretches the cells to equal height, so the inputs
              bottom-align whatever the labels do. */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex h-full flex-col">
              <Label htmlFor="target-opex">OpEx %</Label>
              <div className="mt-auto pt-2">
                <Input
                  id="target-opex"
                  inputMode="decimal"
                  value={opex}
                  onChange={(event) => setOpex(event.target.value)}
                />
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Current: {current.opex != null ? pct(current.opex) : "no prior value"}
                </p>
              </div>
            </div>
            <div className="flex h-full flex-col">
              <Label htmlFor="target-net-margin">Target Net Margin %</Label>
              <div className="mt-auto pt-2">
                <Input
                  id="target-net-margin"
                  inputMode="decimal"
                  value={netMargin}
                  onChange={(event) => setNetMargin(event.target.value)}
                />
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Current:{" "}
                  {current.targetNetMargin != null ? pct(current.targetNetMargin) : "no prior value"}
                </p>
              </div>
            </div>
            <div className="flex h-full flex-col">
              <Label htmlFor="target-commission-rate">Internal Commission Rate %</Label>
              <div className="mt-auto pt-2">
                <Input
                  id="target-commission-rate"
                  inputMode="decimal"
                  value={commissionRate}
                  onChange={(event) => setCommissionRate(event.target.value)}
                />
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Current:{" "}
                  {current.internalCommissionRate != null
                    ? rate(current.internalCommissionRate)
                    : "no prior value"}
                </p>
              </div>
            </div>
          </div>

          <div className="divide-y divide-border border border-border bg-secondary/30">
            <div className="flex items-center justify-between px-3 py-2.5">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                Gross Margin
              </span>
              <span className="text-sm font-semibold tabular-nums text-foreground">
                {pct(previewGrossMargin)}
              </span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                Required Yield
              </span>
              <span className="text-base font-semibold tabular-nums text-primary">
                {pct(preview)}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="target-reason">Reason</Label>
            <Textarea
              id="target-reason"
              value={reason}
              maxLength={1000}
              rows={3}
              placeholder="Why this change, and what it should achieve."
              onChange={(event) => setReason(event.target.value)}
            />
          </div>

          {error ? (
            <p
              role="alert"
              className="border border-decline/50 bg-decline/10 px-3 py-2 text-xs text-decline"
            >
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-xs"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="sm" className="h-9 text-xs" disabled={isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? "Submitting…" : "Submit For Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Reason capture for flipping a office in or out of risk-based pricing. */
export function RbpToggleDialog({
  open,
  onOpenChange,
  office,
  industry,
  primary = "office",
  currentEnabled,
  proposedEnabled,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** OFFICE-LED ladder (primary="office", the default): required, the dominant rung's
   *  name. INDUSTRY-LED ladder (primary="industry"): optional -- undefined means the
   *  industry rung itself, a value means the office rung underneath it. */
  office?: string;
  /** OFFICE-LED ladder: optional -- undefined means the office rung, a value means the
   *  industry rung underneath it. INDUSTRY-LED ladder: required, the dominant rung's
   *  name. */
  industry?: string;
  /** Which dimension is DOMINANT for this call. Defaults to "office" so every existing
   *  call site (the original office-led ladder) is byte-identical in behaviour with no
   *  changes at its call site -- this prop did not exist before, and every place that
   *  doesn't pass it keeps working exactly as it did. */
  primary?: "office" | "industry";
  currentEnabled: boolean;
  proposedEnabled: boolean;
  onSubmit: (draft: TargetProposalDraft) => Promise<unknown> | void;
  isSubmitting: boolean;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setError(null);
  }, [open]);

  // The dominant name for THIS call (what the switch is decided ON), and the optional
  // narrower one underneath it. Both are real, proper names left exactly as stored --
  // title-casing a data value would misrepresent it.
  const dominantName = primary === "office" ? office : industry;
  const narrowName = primary === "office" ? industry : office;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {proposedEnabled ? "Enable" : "Disable"} Risk-Based Pricing — {dominantName}
            {narrowName ? ` / ${narrowName}` : ""}
          </DialogTitle>
          <DialogDescription>
            {/* The two rungs do different things, and the difference is easy to get
                wrong from a switch alone -- so the dialog states which one this is. */}
            {primary === "office" ? (
              industry ? (
                proposedEnabled ? (
                  `${industry} will be priced from its own targets again, instead of ${office}'s office-wide ones.`
                ) : (
                  `${industry} will be priced from ${office}'s office-wide targets instead of its own. It stays risk-based priced.`
                )
              ) : proposedEnabled ? (
                `Every industry in ${office} becomes risk-based priced again, subject to its own setting.`
              ) : (
                `Nothing in ${office} will be risk-based priced, whatever its industries are set to.`
              )
            ) : office ? (
              proposedEnabled ? (
                `${office} will be priced from its own targets again, instead of ${industry}'s industry-wide ones.`
              ) : (
                `${office} will be priced from ${industry}'s industry-wide targets instead of its own. It stays risk-based priced.`
              )
            ) : proposedEnabled ? (
              `Every office pricing ${industry} becomes risk-based priced again, subject to its own setting.`
            ) : (
              `Nothing priced under ${industry} will be risk-based priced, whatever its offices are set to.`
            )}{" "}
            This goes through the same approval queue as any other proposal, and is logged
            permanently. No second approval is forced.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="rbp-reason">Reason</Label>
          <Textarea
            id="rbp-reason"
            rows={3}
            maxLength={1000}
            value={reason}
            placeholder={
              primary === "office"
                ? "Why this office is moving in or out of risk-based pricing."
                : "Why this industry is moving in or out of risk-based pricing."
            }
            onChange={(event) => setReason(event.target.value)}
          />
          {error ? (
            <p
              role="alert"
              className="border border-decline/50 bg-decline/10 px-3 py-2 text-xs text-decline"
            >
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-xs"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-9 text-xs"
            disabled={isSubmitting}
            onClick={() => {
              if (!reason.trim()) {
                setError("Add a reason — this change is logged.");
                return;
              }
              void onSubmit({
                kind: "rbp-toggle",
                // The rung this switch belongs to. Approval writes rbp_enabled to exactly
                // this scope and nothing else -- the ladder inherits downward rather than
                // fanning the value out, so a narrower rung survives a dominant-rung
                // toggle. ALL_RATINGS always: ratings carry no switch.
                //
                // primary="office" (unchanged from before this prop existed): office is
                // the dominant, required name; industry falls back to ALL_INDUSTRIES.
                // primary="industry": industry is the dominant, required name; office
                // falls back to ALL_OFFICES -- the schema already allows office='All
                // Offices' paired with any industry (target_policies_all_offices_ck only
                // ties the sentinel to office_number, not to industry), and the write
                // path (_apply_rbp_toggle) needed no changes to accept it.
                office: primary === "office" ? office! : (office ?? ALL_OFFICES),
                industry: primary === "office" ? (industry ?? ALL_INDUSTRIES) : industry!,
                rating: ALL_RATINGS,
                currentOpex: null,
                proposedOpex: 0,
                currentTargetNetMargin: null,
                proposedTargetNetMargin: 0,
                currentInternalCommissionRate: null,
                proposedInternalCommissionRate: 0,
                currentRbpEnabled: currentEnabled,
                proposedRbpEnabled: proposedEnabled,
                reason: reason.trim(),
              });
            }}
          >
            {isSubmitting ? "Submitting…" : "Submit For Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReadOnlyValue({ label, value }: { label: string; value: string }) {
  // Same alignment problem as the input row below: "Expected Loss (Calibration)" wraps
  // to two lines where "External Commission" does not, which dropped its value half a
  // line below its neighbours. Full-height flex column + mt-auto bottom-aligns the
  // values across the grid without assuming how many lines any label takes.
  return (
    <div className="flex h-full flex-col">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-auto pt-0.5 text-sm font-medium tabular-nums text-foreground">{value}</p>
    </div>
  );
}

export function TargetApprovalQueue({
  proposals,
  requiresSecondApproval,
  onDecide,
  isDeciding,
  canDecide,
}: {
  proposals: TargetProposal[];
  requiresSecondApproval: (proposal: TargetProposal) => boolean;
  onDecide: (id: string, status: "approved" | "rejected", notes: string) => Promise<unknown> | void;
  isDeciding: boolean;
  /** Contributor and above. Approve and Reject share one endpoint
   *  (POST /target-proposals/{id}/decide), so they share one gate. A reviewer still sees
   *  the queue -- they raised the proposals in it -- but cannot decide. */
  canDecide: boolean;
}) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const pending = useMemo(
    () => proposals.filter((proposal) => proposal.status === "pending"),
    [proposals],
  );

  function decide(proposal: TargetProposal, status: "approved" | "rejected") {
    const note = (notes[proposal.id] ?? "").trim();
    if (!note) {
      setErrors((prev) => ({
        ...prev,
        [proposal.id]: "Add review notes before approving or rejecting.",
      }));
      return;
    }
    setErrors((prev) => ({ ...prev, [proposal.id]: "" }));
    void onDecide(proposal.id, status, note);
  }

  if (pending.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing waiting for review.</p>;
  }

  return (
    <ul className="divide-y divide-border/60 border border-border bg-card">
      {pending.map((proposal) => (
        <li key={proposal.id} className="space-y-3 p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-[18rem] flex-1">
              <p className="text-sm font-medium text-foreground">
                {proposal.office} · {proposal.industry} · {proposal.rating}
              </p>
              {proposal.kind === "rbp-toggle" ? (
                <p className="mt-1 text-xs text-foreground/90">
                  Risk-based pricing {proposal.currentRbpEnabled ? "enabled" : "disabled"} →{" "}
                  <span className="font-semibold">
                    {proposal.proposedRbpEnabled ? "enabled" : "disabled"}
                  </span>
                </p>
              ) : (
                <p className="mt-1 text-xs tabular-nums text-foreground/90">
                  OpEx {proposal.currentOpex != null ? pct(proposal.currentOpex) : "—"} →{" "}
                  {pct(proposal.proposedOpex)} · Target net margin{" "}
                  {proposal.currentTargetNetMargin != null
                    ? pct(proposal.currentTargetNetMargin)
                    : "—"}{" "}
                  → {pct(proposal.proposedTargetNetMargin)} · Internal commission{" "}
                  {proposal.currentInternalCommissionRate != null
                    ? rate(proposal.currentInternalCommissionRate)
                    : "—"}{" "}
                  → {rate(proposal.proposedInternalCommissionRate)} · Gross margin{" "}
                  {pct(
                    grossMarginFor(
                      proposal.proposedTargetNetMargin,
                      proposal.proposedInternalCommissionRate,
                    ),
                  )}
                </p>
              )}
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {proposal.reason}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Proposed by {proposal.proposedBy}
              </p>
              {requiresSecondApproval(proposal) ? (
                <p className="mt-2 inline-flex items-center border border-primary/40 bg-amber-dim px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                  Second Approval Required
                </p>
              ) : null}
            </div>
            {canDecide && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  disabled={isDeciding}
                  onClick={() => decide(proposal, "rejected")}
                >
                  Reject
                </Button>
                <Button
                  size="sm"
                  className="h-8 text-xs"
                  disabled={isDeciding}
                  onClick={() => decide(proposal, "approved")}
                >
                  Approve
                </Button>
              </div>
            )}
          </div>
          {/* Notes belong to the decision, so they follow the same gate -- a reviewer
              would otherwise be typing into a box with no button to submit it. */}
          {canDecide && (
            <Textarea
              rows={2}
              maxLength={1000}
              placeholder="Review Notes (Required)"
              value={notes[proposal.id] ?? ""}
              onChange={(event) =>
                setNotes((prev) => ({ ...prev, [proposal.id]: event.target.value }))
              }
            />
          )}
          {errors[proposal.id] ? (
            <p
              role="alert"
              className="border border-decline/50 bg-decline/10 px-3 py-2 text-xs text-decline"
            >
              {errors[proposal.id]}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

const STATUS_CLASSES: Record<TargetLogEntry["status"], string> = {
  APPROVED: "border-approve/40 bg-approve/10 text-approve",
  REJECTED: "border-decline/40 bg-decline/10 text-decline",
  SUPERSEDED: "border-border bg-secondary text-muted-foreground",
};

export function TargetLogTable({ entries }: { entries: TargetLogEntry[] }) {
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No decisions logged yet.</p>;
  }

  return (
    <div className="overflow-x-auto border border-border bg-card">
      <table className="w-full min-w-[1520px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            {[
              "Office Name",
              "Industry",
              "Rating",
              "OpEx",
              "Target Net Margin",
              "Internal Commission Rate",
              "RBP",
              "Effective From",
              "Rows Updated",
              "Reference",
              "Deals Priced",
              "Proposed By",
              "Reason",
              "Reviewed By",
              "Status",
            ].map((head) => (
              <th
                key={head}
                className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className="border-b border-border/60 align-top last:border-b-0">
              <td className="px-3 py-2.5 font-medium text-foreground">{entry.office}</td>
              <td className="px-3 py-2.5 text-foreground/90">{entry.industry}</td>
              <td className="px-3 py-2.5 text-foreground/90">{entry.rating}</td>
              <td className="px-3 py-2.5 tabular-nums text-foreground/90">
                {entry.kind === "rbp-toggle"
                  ? "—"
                  : `${entry.oldOpex != null ? pct(entry.oldOpex) : "—"} → ${pct(entry.newOpex)}`}
              </td>
              <td className="px-3 py-2.5 tabular-nums text-foreground/90">
                {entry.kind === "rbp-toggle"
                  ? "—"
                  : `${
                      entry.oldTargetNetMargin != null ? pct(entry.oldTargetNetMargin) : "—"
                    } → ${pct(entry.newTargetNetMargin)}`}
              </td>
              <td className="px-3 py-2.5 tabular-nums text-foreground/90">
                {entry.kind === "rbp-toggle"
                  ? "—"
                  : `${
                      entry.oldInternalCommissionRate != null
                        ? rate(entry.oldInternalCommissionRate)
                        : "—"
                    } → ${rate(entry.newInternalCommissionRate)}`}
              </td>
              <td className="px-3 py-2.5 text-xs text-foreground/90">
                {entry.newRbpEnabled == null
                  ? "—"
                  : `${entry.oldRbpEnabled ? "on" : "off"} → ${entry.newRbpEnabled ? "on" : "off"}`}
              </td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">
                {format(new Date(entry.effectiveFrom), "d MMM yyyy")}
              </td>
              <td className="px-3 py-2.5 tabular-nums text-foreground/90">
                {/* How far the approval's fan-out reached: 1 for a single
                    combination, the whole grid for an All/All/All approval. */}
                {entry.rowsUpdated ?? "—"}
              </td>
              <td className="px-3 py-2.5 font-mono text-[11px] text-muted-foreground">
                {entry.policyVersionId}
              </td>
              <td className="px-3 py-2.5 tabular-nums text-foreground/90">
                {entry.dealsPriced.toLocaleString("en-US")}
              </td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">{entry.proposedBy}</td>
              <td className="max-w-[22rem] px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
                {entry.reason}
              </td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">
                {entry.reviewedBy ?? "—"}
              </td>
              <td className="px-3 py-2.5">
                <span
                  className={cn(
                    "inline-flex items-center border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider",
                    STATUS_CLASSES[entry.status],
                  )}
                >
                  {entry.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
        Deal counts default to 0 until DealPricingRecord exists. "Rows Updated" is how many
        combinations in the policy grid the approval rewrote.
      </p>
    </div>
  );
}

export function SyncStatusFooter({ sync }: { sync: SyncStatus | undefined }) {
  const fmt = (value: string | null | undefined) =>
    value ? format(new Date(value), "d MMM yyyy, HH:mm") : "never";

  return (
    <p className="mt-8 border-t border-border pt-4 text-xs text-muted-foreground">
      Last sent to UE: {fmt(sync?.lastSentToUE)} · Last sent to Salesforce:{" "}
      {fmt(sync?.lastSentToSalesforce)}
    </p>
  );
}
